/**
 * Pure keyboard-gating helpers shared by TextInput + Menu. Free of Phaser/DOM so the
 * load-bearing logic is unit-testable in a plain node test environment.
 *
 * THE BUG THESE SOLVE: the overworld registers WASD/arrows via Phaser
 * `createCursorKeys()`/`addKey()`, and the menus capture Space/Enter. Phaser's keyboard
 * plugin `preventDefault()`s those *captured* keys, and the Menu window-listener
 * `preventDefault()`s arrows/Enter/Space/Esc too. So when a real hidden DOM <input> is
 * focused (the chat box / trainer-name field), those keystrokes are swallowed before the
 * browser delivers them to the input — letters drop (famously W/A/S/D and SPACE).
 *
 * Fix shape:
 *   - TextInput, on focus, calls `suppressGameKeyboard(kb)` (disable global capture so
 *     keys stop being prevent-defaulted, AND disable the plugin so the player doesn't
 *     walk while typing); on blur/destroy it calls `restoreGameKeyboard(kb, prev)`.
 *   - Menu's key handler bails early when `domTextFieldFocused(activeEl)` is true.
 */

/** The minimal slice of Phaser's KeyboardPlugin these helpers touch. */
export interface GameKeyboard {
  enabled: boolean;
  disableGlobalCapture(): unknown;
  enableGlobalCapture(): unknown;
}

/** Snapshot returned by `suppressGameKeyboard`, fed back to `restoreGameKeyboard`. */
export interface KeyboardSuppression {
  /** The plugin's `enabled` value before suppression, so restore is exact. */
  readonly prevEnabled: boolean;
}

/**
 * Stop the game keyboard from eating keystrokes while a DOM input is focused:
 *   1. `disableGlobalCapture()` — captured keys (WASD/arrows/space/enter) stop being
 *      prevent-defaulted, so every keystroke reaches the focused <input>.
 *   2. `enabled = false` — Phaser stops processing keydown, so the player doesn't walk
 *      or interact while typing.
 * Returns the prior `enabled` flag so it can be restored exactly. Idempotent at the call
 * sites via their own `suppressed` guard.
 */
export function suppressGameKeyboard(kb: GameKeyboard): KeyboardSuppression {
  const prevEnabled = kb.enabled;
  kb.disableGlobalCapture();
  kb.enabled = false;
  return { prevEnabled };
}

/** Re-arm the game keyboard once typing ends — the exact inverse of suppression. */
export function restoreGameKeyboard(kb: GameKeyboard, snapshot: KeyboardSuppression): void {
  kb.enabled = snapshot.prevEnabled;
  kb.enableGlobalCapture();
}

/**
 * Is the given active element a DOM text field the player is typing into? Menu key
 * handlers must bail when this is true so they don't consume / preventDefault keystrokes
 * meant for the input. Takes the element (or its tag) so it's testable without a real DOM.
 */
export function domTextFieldFocused(
  active: { tagName?: string; isContentEditable?: boolean } | null | undefined,
): boolean {
  if (!active) return false;
  const tag = active.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || active.isContentEditable === true;
}
